import { useRef, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CheckCircle2, MessageSquare, FileText, BarChart3, ChevronRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const tabs = [
  { id: 'milestones', label: 'Milestones', icon: CheckCircle2 },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'reports', label: 'Reports', icon: FileText },
];

const milestones = [
  { name: 'Kickoff checklist completed', status: 'completed', date: 'Jan 15' },
  { name: 'Security audit delivered', status: 'completed', date: 'Jan 22' },
  { name: 'Weekly report submitted', status: 'in-progress', date: 'Jan 29' },
  { name: 'Architecture review', status: 'pending', date: 'Feb 5' },
];

export default function WorkspaceSection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const milestonesRef = useRef<(HTMLDivElement | null)[]>([]);
  const [activeTab, setActiveTab] = useState('milestones');

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=130%',
          pin: true,
          scrub: 0.6,
        },
      });

      // Phase 1 (0-30%): Entrance
      scrollTl.fromTo(
        headlineRef.current,
        { x: '-18vw', opacity: 0 },
        { x: 0, opacity: 1, ease: 'none' },
        0
      );

      scrollTl.fromTo(
        workspaceRef.current,
        { x: '18vw', opacity: 0 },
        { x: 0, opacity: 1, ease: 'none' },
        0.06
      );

      scrollTl.fromTo(
        tabsRef.current,
        { y: -10, opacity: 0 },
        { y: 0, opacity: 1, ease: 'none' },
        0.18
      );

      milestonesRef.current.forEach((milestone, index) => {
        if (milestone) {
          scrollTl.fromTo(
            milestone,
            { y: 16, opacity: 0 },
            { y: 0, opacity: 1, ease: 'none' },
            0.2 + index * 0.025
          );
        }
      });

      // Phase 2 (30-70%): Settle

      // Phase 3 (70-100%): Exit
      scrollTl.fromTo(
        headlineRef.current,
        { y: 0, opacity: 1 },
        { y: '-12vh', opacity: 0, ease: 'power2.in' },
        0.7
      );

      scrollTl.fromTo(
        workspaceRef.current,
        { x: 0, y: 0, opacity: 1 },
        { x: '-10vw', y: '10vh', opacity: 0, ease: 'power2.in' },
        0.7
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-pinned bg-navy flex items-center z-[70]"
    >
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Headline */}
          <div ref={headlineRef} className="opacity-0">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
              Workspace
            </span>
            <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white mb-6">
              A workspace built for executive delivery
            </h2>
            <p className="text-body-lg text-slate mb-8 max-w-lg">
              Milestones, messaging, documents, and impact tracking—without the
              tool sprawl.
            </p>
            <ul className="space-y-4 mb-8">
              {milestones.slice(0, 3).map((milestone) => (
                <li key={milestone.name} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-teal" />
                  <span className="text-slate">{milestone.name}</span>
                </li>
              ))}
            </ul>
            <button 
              onClick={() => navigate('/register')}
              className="btn-primary flex items-center gap-2"
            >
              See the workspace
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Right - Workspace Card */}
          <div ref={workspaceRef} className="opacity-0">
            <div className="surface-panel overflow-hidden">
              {/* Tabs */}
              <div
                ref={tabsRef}
                className="flex border-b border-white/10 opacity-0"
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'text-teal border-b-2 border-teal'
                        : 'text-slate hover:text-white'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="p-6">
                {activeTab === 'milestones' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-heading text-sm font-semibold text-white">
                        Project Milestones
                      </h4>
                      <span className="text-xs text-slate">3 of 4 complete</span>
                    </div>
                    {milestones.map((milestone, index) => (
                      <div
                        key={milestone.name}
                        ref={(el) => { milestonesRef.current[index] = el; }}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg opacity-0"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              milestone.status === 'completed'
                                ? 'bg-teal'
                                : milestone.status === 'in-progress'
                                ? 'bg-warning'
                                : 'bg-white/20'
                            }`}
                          />
                          <span
                            className={`text-sm ${
                              milestone.status === 'completed'
                                ? 'text-slate line-through'
                                : 'text-white'
                            }`}
                          >
                            {milestone.name}
                          </span>
                        </div>
                        <span className="text-xs font-mono text-slate">
                          {milestone.date}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'messages' && (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-teal/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-teal">DK</span>
                      </div>
                      <div className="flex-1 bg-white/5 rounded-lg p-3">
                        <p className="text-sm text-white">
                          Weekly report is ready for review. Let me know if you
                          have any questions!
                        </p>
                        <span className="text-xs text-slate mt-2 block">
                          2 hours ago
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 flex-row-reverse">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">You</span>
                      </div>
                      <div className="flex-1 bg-teal/10 rounded-lg p-3">
                        <p className="text-sm text-white">
                          Thanks David, looking great. Can we discuss the
                          architecture section in tomorrow's call?
                        </p>
                        <span className="text-xs text-slate mt-2 block">
                          1 hour ago
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'reports' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-heading text-sm font-semibold text-white">
                        Weekly Reports
                      </h4>
                      <BarChart3 className="w-4 h-4 text-slate" />
                    </div>
                    {['Week 3 Report', 'Week 2 Report', 'Week 1 Report'].map(
                      (report) => (
                        <div
                          key={report}
                          className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-teal" />
                            <span className="text-sm text-white">{report}</span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate" />
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
